import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import type { Patient } from "services/types/patients.type";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

/**
 * `practiceBannerHidden` is a per-browser preference, not a per-user one: it is
 * offered only to internal Suno staff on their own machine, and the thing it
 * hides (the practice context banner) is chrome rather than data. Keying it by
 * user id would buy nothing and cost a second localStorage namespace.
 */
const practiceBannerStorage = setupLocalStorageAPI(
  "quickLauncher.practiceBannerHidden"
);

/**
 * Same kind of preference, the other way round: the calls workspace's "Hidden"
 * chip (ENG-2364) is a Suno-only control, and Suno staff demo the app from Suno
 * accounts, so the chip stays away until a Suno user asks for it here. Off, the
 * Calls page looks as it does to the practice.
 */
const hiddenCallsChipStorage = setupLocalStorageAPI(
  "quickLauncher.hiddenCallsChipShown"
);

const positionStorage = setupLocalStorageAPI("quickLauncher.position");

/**
 * A drag offset from the panel's natural (centred, `mt: 12`) layout position —
 * react-draggable's own coordinate space — not a viewport coordinate. Stored
 * that way so the panel keeps its *relative* placement when the window resizes;
 * `QuickLauncherPaper` clamps it back inside the viewport on open, which is what
 * makes a position saved on a large monitor safe to restore on a laptop.
 */
export interface QuickLauncherPosition {
  x: number;
  y: number;
}

const isValidPosition = (value: unknown): value is QuickLauncherPosition =>
  typeof value === "object" &&
  value !== null &&
  Number.isFinite((value as QuickLauncherPosition).x) &&
  Number.isFinite((value as QuickLauncherPosition).y);

/** How the panel was last opened — read by the "opened" telemetry event. */
export type QuickLauncherOpenTrigger =
  | "hotkey"
  | "help"
  | "side_menu"
  | "unknown";

/**
 * A request for the launcher's own appointment form (see
 * `quick-launcher-hosts.component.tsx`). `nonce` changes on every request so
 * asking twice in a row reopens the form; `patient` pre-selects one.
 */
export interface QuickLauncherAppointmentRequest {
  nonce: number;
  patient?: Patient;
}

/** A request for the launcher's own "New alert" dialog, cleared when it closes. */
export interface QuickLauncherAlertRequest {
  patientId: number;
  patientName?: string;
}

/**
 * A request to capture the current page (see `screenshot/`). Carries nothing
 * but a `nonce`, because the thing being captured is "whatever is on screen a
 * moment from now" rather than anything the action could name — and the nonce
 * is what makes a second ⌘K → Screenshot re-capture instead of re-showing the
 * previous image.
 */
export interface QuickLauncherScreenshotRequest {
  nonce: number;
}

export interface QuickLauncherState {
  open: boolean;
  openTrigger: QuickLauncherOpenTrigger;
  practiceBannerHidden: boolean;
  hiddenCallsChipShown: boolean;
  position: QuickLauncherPosition | null;
  appointmentRequest: QuickLauncherAppointmentRequest | null;
  alertRequest: QuickLauncherAlertRequest | null;
  screenshotRequest: QuickLauncherScreenshotRequest | null;
  /** The "Keyboard shortcuts" guide dialog (account menu + launcher entry). */
  helpOpen: boolean;
}

const readStoredPosition = (): QuickLauncherPosition | null => {
  const stored = positionStorage.getData(null);
  return isValidPosition(stored) ? stored : null;
};

const initialState: QuickLauncherState = {
  open: false,
  openTrigger: "unknown",
  // `getData` returns the fallback for a malformed/absent entry, so a hostile or
  // stale value can only ever resolve to "shown".
  practiceBannerHidden: Boolean(practiceBannerStorage.getData(false)),
  hiddenCallsChipShown: Boolean(hiddenCallsChipStorage.getData(false)),
  position: readStoredPosition(),
  appointmentRequest: null,
  alertRequest: null,
  screenshotRequest: null,
  helpOpen: false
};

export /**
 * A request's next nonce: one more than the current request's, so it changes on every
 * request however fast they come, and the reducer stays pure. After a dismissal the count
 * restarts, which is fine: the key still changes, through `null`.
 */
const nextNonce = (current: { nonce: number } | null | undefined) =>
  (current?.nonce ?? 0) + 1;

const quickLauncherSlice = createSlice({
  name: "quickLauncher",
  initialState,
  reducers: {
    // The optional payload names what opened the panel; it is recorded only on
    // the closed → open transition so a toggle that closes leaves the previous
    // trigger alone.
    openQuickLauncher: (
      state,
      action: PayloadAction<QuickLauncherOpenTrigger | undefined>
    ) => {
      state.open = true;
      state.openTrigger = action.payload ?? "unknown";
    },
    closeQuickLauncher: (state) => {
      state.open = false;
    },
    toggleQuickLauncher: (
      state,
      action: PayloadAction<QuickLauncherOpenTrigger | undefined>
    ) => {
      state.open = !state.open;
      if (state.open) state.openTrigger = action.payload ?? "unknown";
    },
    setPracticeBannerHidden: (state, action: PayloadAction<boolean>) => {
      state.practiceBannerHidden = action.payload;
      practiceBannerStorage.updateData(action.payload);
    },
    togglePracticeBannerHidden: (state) => {
      state.practiceBannerHidden = !state.practiceBannerHidden;
      practiceBannerStorage.updateData(state.practiceBannerHidden);
    },
    toggleHiddenCallsChipShown: (state) => {
      state.hiddenCallsChipShown = !state.hiddenCallsChipShown;
      hiddenCallsChipStorage.updateData(state.hiddenCallsChipShown);
    },
    setQuickLauncherPosition: (
      state,
      action: PayloadAction<QuickLauncherPosition>
    ) => {
      const { x, y } = action.payload;
      // Ignore a non-finite drag result rather than persisting a value that
      // would translate the panel off-screen on every future open.
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      state.position = { x, y };
      positionStorage.updateData(state.position);
    },
    resetQuickLauncherPosition: (state) => {
      state.position = null;
      positionStorage.clear();
    },
    requestNewAppointment: (
      state,
      action: PayloadAction<{ patient?: Patient } | undefined>
    ) => {
      state.appointmentRequest = {
        // A counter, not `Date.now()`: two requests in the same millisecond shared a
        // timestamp and the host ignored the second (it reruns on a nonce change).
        nonce: nextNonce(state.appointmentRequest),
        patient: action.payload?.patient
      };
    },
    requestNewAlert: (
      state,
      action: PayloadAction<QuickLauncherAlertRequest>
    ) => {
      state.alertRequest = action.payload;
    },
    dismissAlertRequest: (state) => {
      state.alertRequest = null;
    },
    requestScreenshot: (state) => {
      state.screenshotRequest = { nonce: nextNonce(state.screenshotRequest) };
    },
    dismissScreenshotRequest: (state) => {
      state.screenshotRequest = null;
    },
    openQuickLauncherHelp: (state) => {
      state.helpOpen = true;
    },
    closeQuickLauncherHelp: (state) => {
      state.helpOpen = false;
    }
  }
});

export const {
  openQuickLauncher,
  closeQuickLauncher,
  toggleQuickLauncher,
  setPracticeBannerHidden,
  togglePracticeBannerHidden,
  toggleHiddenCallsChipShown,
  setQuickLauncherPosition,
  resetQuickLauncherPosition,
  requestNewAppointment,
  requestNewAlert,
  dismissAlertRequest,
  requestScreenshot,
  dismissScreenshotRequest,
  openQuickLauncherHelp,
  closeQuickLauncherHelp
} = quickLauncherSlice.actions;

const quickLauncherSelector = (state: RootState) => state.quickLauncher;

export const selectQuickLauncherOpen = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.open
);

export const selectQuickLauncherOpenTrigger = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.openTrigger
);

export const selectPracticeBannerHidden = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.practiceBannerHidden
);

export const selectHiddenCallsChipShown = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.hiddenCallsChipShown
);

export const selectQuickLauncherPosition = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.position
);

export const selectQuickLauncherAppointmentRequest = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.appointmentRequest
);

export const selectQuickLauncherAlertRequest = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.alertRequest
);

export const selectQuickLauncherScreenshotRequest = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.screenshotRequest
);

export const selectQuickLauncherHelpOpen = createSelector(
  quickLauncherSelector,
  (quickLauncher) => quickLauncher.helpOpen
);

export default quickLauncherSlice.reducer;
